import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isOnline, OFFLINE_STATUS_EVENT, subscribeNetwork } from '../services/offline/network';
import { countPendingOutbox } from '../services/offline/outbox';
import { drainOutbox, retryFailedOutbox, startSyncEngine } from '../services/offline/syncEngine';

const OfflineContext = createContext(null);

export function OfflineProvider({ children }) {
  const [online, setOnline] = useState(() => isOnline());
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refreshPending = useCallback(async () => {
    setPending(await countPendingOutbox());
  }, []);

  useEffect(() => {
    startSyncEngine();
    refreshPending();
    const stopNet = subscribeNetwork((next) => {
      setOnline(next);
      if (next) {
        setSyncing(true);
        drainOutbox().finally(() => {
          setSyncing(false);
          refreshPending();
        });
      }
    });
    const onStatus = (event) => {
      if (typeof event.detail?.pending === 'number') {
        setPending(event.detail.pending);
      } else {
        refreshPending();
      }
    };
    window.addEventListener(OFFLINE_STATUS_EVENT, onStatus);
    return () => {
      stopNet();
      window.removeEventListener(OFFLINE_STATUS_EVENT, onStatus);
    };
  }, [refreshPending]);

  const retry = useCallback(async () => {
    setSyncing(true);
    try {
      await retryFailedOutbox();
    } finally {
      setSyncing(false);
      await refreshPending();
    }
  }, [refreshPending]);

  const value = useMemo(
    () => ({ online, pending, syncing, retry }),
    [online, pending, syncing, retry],
  );

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

export function useOfflineStatus() {
  const ctx = useContext(OfflineContext);
  if (!ctx) {
    return { online: isOnline(), pending: 0, syncing: false, retry: async () => {} };
  }
  return ctx;
}
